import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import { Alert, AlertTitle, Link, Stack, Typography } from '@mui/material'
import { signInBodySchema, SignUpBody } from '@shared/dto/auth.dto'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import FundingLogos from '../../components/common/FundingLogos'
import { PasswordInput } from '../../components/forms/PasswordInput'
import { TextInput } from '../../components/forms/TextInput'
import { LoaderButton } from '../../components/LoaderButton'
import { useSignInMutation } from '../../hooks/useMutations/useAuthMutations'

export default function SignIn() {
  const { t } = useTranslation()
  const signInMutation = useSignInMutation()
  const navigate = useNavigate()
  const [alertData, setAlertData] = useState<{
    title: string
    description: string
  } | null>(null)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<SignUpBody>({
    defaultValues: {
      email: '',
      password: ''
    },
    resolver: standardSchemaResolver(signInBodySchema),
    mode: 'onBlur'
  })

  const email = watch('email')
  const password = watch('password')

  const onSubmit = async (data: SignUpBody) => {
    signInMutation.mutate(data, {
      onSuccess: () => {
        navigate('/companies')
      },
      onError: (error: any) => {
        if (error.response.data.data.reason === 'UserNotConfirmedException') {
          navigate(`/sign-up/confirm?email=${email}`)
          return
        }
        setAlertData({
          title: t('sign_in.error.title'),
          description: t('sign_in.error.description')
        })
      }
    })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack
        direction='column'
        spacing='1rem'
        sx={{
          width: '37.875rem',
          minHeight: '39rem',
          padding: '2.5rem',
          borderRadius: '1.5rem',
          backgroundColor: 'background.default',
          justifyContent: 'space-between'
        }}
      >
        <Stack direction='column' spacing='1.5rem'>
          <Typography variant='h4'>{t('sign_in.title')}</Typography>
          <Typography variant='h6' color='text.secondary'>
            {t('sign_up.subtitle')}
          </Typography>
          <TextInput
            name='email'
            register={register}
            errors={errors}
            label={t('sign_in.email')}
          />
          <PasswordInput
            name='password'
            register={register}
            errors={errors}
            label={t('sign_in.password')}
          />
          <LoaderButton
            type='submit'
            disabled={(!email || !password) && !signInMutation.isPending}
            loading={signInMutation.isPending}
            startIcon={<ChevronLeftIcon />}
            label={
              signInMutation.isPending
                ? t('sign_in.button_loading')
                : t('sign_in.button')
            }
            sx={{
              textTransform: 'none',
              padding: '0.5rem 2rem'
            }}
          />
          <Alert
            severity='error'
            sx={{
              visibility: alertData ? 'visible' : 'hidden',
              color: 'rgba(95, 33, 32, 1)',
              backgroundColor: '#FDEDED'
            }}
          >
            <AlertTitle>{alertData?.title || ''}</AlertTitle>
            {alertData?.description || ''}
          </Alert>
        </Stack>

        <Stack direction='column' spacing='0.5rem' alignItems='flex-start'>
          <Typography variant='subtitle1' color='textSecondary'>
            {t('sign_in.not_registered')}{' '}
            <Link href='/sign-up'>{t('sign_in.create_account')}</Link>
          </Typography>
          <Typography variant='subtitle1' color='textSecondary'>
            {t('sign_in.forgot_password')}{' '}
            <Link href='/forgot-password'>{t('sign_in.restore_password')}</Link>
          </Typography>
        </Stack>
        <FundingLogos size='small' sx={{ mt: '1.5rem' }} />
      </Stack>
    </form>
  )
}
